'use client'

import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'
import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { getBaseImageUrl } from '@/constants/hooks'
import { EventBannerResponse, PromotionBannerResponse, SubPromotionBanner } from '@/services/dataTypes'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
    sliders: EventBannerResponse[] | PromotionBannerResponse[] | SubPromotionBanner[]
    domain: string
    eventName: string
}

const BannerImage = ({ src, alt, domain, priority = false, loading }: { src: string; alt: string; domain: string; priority?: boolean; loading?: 'eager' | 'lazy' }) => {
    const [loaded, setLoaded] = useState(false)
    return (
        <div className="relative w-full">
            {!loaded && <div className="absolute inset-0 w-full h-64 bg-[#cce3de]/40 animate-pulse rounded-xl" />}
            <Image
                src={getBaseImageUrl(domain, src, '')}
                height={500}
                width={1400}
                alt={alt}
                onLoad={() => setLoaded(true)}
                className={`w-full h-auto rounded-xl transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                priority={priority}
                loading={loading}
            />
        </div>
    )
}

const EventSlider = ({ sliders, domain, eventName }: Props) => {
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({ loop: true, slides: { perView: 1 } })
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const start = () => {
        if (sliders.length <= 1) return
        timerRef.current = setInterval(() => slider.current?.next(), 4000)
    }
    const stop = () => { if (timerRef.current) clearInterval(timerRef.current) }

    useEffect(() => {
        if (!slider.current) return
        start()
        return () => stop()
    }, [slider, sliders.length])

    if (sliders?.length <= 1) {
        return sliders[0] ? (
            <BannerImage src={sliders[0].banner} alt={`${eventName} Banner`} domain={domain} priority loading="eager" />
        ) : null
    }

    return (
        <div className="relative" onMouseEnter={stop} onMouseLeave={start}>
            <div ref={sliderRef} className="keen-slider">
                {sliders.map((slide, i) => (
                    <div className="keen-slider__slide" key={i}>
                        <BannerImage
                            src={slide.banner}
                            alt={`${eventName} Banner ${i + 1}`}
                            domain={domain}
                            priority={i === 0}
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    </div>
                ))}
            </div>

            <button
                onClick={() => slider.current?.prev()}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all z-10 hover:scale-110"
            >
                <ChevronLeft size={18} className="text-[#00a87a]" />
            </button>
            <button
                onClick={() => slider.current?.next()}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all z-10 hover:scale-110"
            >
                <ChevronRight size={18} className="text-[#00a87a]" />
            </button>
        </div>
    )
}

export default EventSlider
